import type { Address } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';
import { referralManagerAbi } from '@syntra/abi';
import { DEPLOYMENT as D } from '../config/env.ts';
import { rankProgress, type NodeData } from '../pages/Referral.tsx';

const ZERO = '0x0000000000000000000000000000000000000000';

export interface RankStep {
  rank: number; // current rank, 0 = member
  rateBps: number; // the current rank's binary rate (0 for a member)
  next?: {
    rank: number;
    rateBps: number;
    thresholdUsd: bigint; // both legs' active volume, held at the same time
    weakerUsd: bigint; // the weaker leg must reach this (minSideBps of the threshold)
    minSideBps: number;
  };
  totalUsd: bigint; // both legs' active volume now (ranks are measured on it, R-1)
  weakerLegUsd: bigint; // the smaller of the two legs' active volume
  progress: number; // 0..1 towards the next rank: the lesser of total volume and the weaker side
  totalProgress: number;
  weakerProgress: number;
}

/** Where a wallet stands on the referral ranks and how far it is from the next one. */
export function useRank(address: Address | undefined): RankStep | undefined {
  const node = useReadContract({
    address: D.referral,
    abi: referralManagerAbi,
    functionName: 'node',
    args: [address ?? ZERO],
    query: { enabled: Boolean(address), refetchInterval: 30_000 },
  });
  const minSide = useReadContract({ address: D.referral, abi: referralManagerAbi, functionName: 'minSideBps' });
  const cfg = useReadContracts({
    allowFailure: false,
    contracts: [1, 2, 3, 4, 5].map((r) => ({ address: D.referral, abi: referralManagerAbi, functionName: 'rankConfig', args: [r] }) as const),
  });
  const n = node.data as NodeData | undefined;
  if (!address || !n || !cfg.data || minSide.data === undefined) return undefined;
  return rankStep(n, cfg.data as readonly { thresholdUsd: bigint; rateBps: number }[], BigInt(minSide.data));
}

/**
 * The rank step from a node and the rank table. Progress is measured on the volume both legs hold now, as the contract
 * measures ranks (R-1): staking that was withdrawn no longer counts towards the next rank.
 */
export function rankStep(n: NodeData, ranks: readonly { thresholdUsd: bigint; rateBps: number }[], side: bigint): RankStep {
  const rank = Number(n.rank);
  const rateBps = rank > 0 ? Number(ranks[rank - 1].rateBps) : 0;
  const totalUsd = n.leftActiveUsd + n.rightActiveUsd;
  const weakerLegUsd = n.leftActiveUsd < n.rightActiveUsd ? n.leftActiveUsd : n.rightActiveUsd;
  if (rank >= 5) return { rank, rateBps, totalUsd, weakerLegUsd, progress: 1, totalProgress: 1, weakerProgress: 1 };
  const next = ranks[rank];
  const pr = rankProgress(n.leftActiveUsd, n.rightActiveUsd, next.thresholdUsd, side);
  return {
    rank,
    rateBps,
    next: { rank: rank + 1, rateBps: Number(next.rateBps), thresholdUsd: next.thresholdUsd, weakerUsd: (next.thresholdUsd * side) / 10_000n, minSideBps: Number(side) },
    totalUsd,
    weakerLegUsd,
    progress: Math.min(pr.total, pr.weaker),
    totalProgress: pr.total,
    weakerProgress: pr.weaker,
  };
}
